/* ═══════════════════════════════════════════════════════════════
   TODAY — the pure formatting helpers behind the Today tab.

   Everything here turns the GET /api/today payload (design
   2026-09-08 §2) into the words the screen shows. No React, no
   fetch, no Date.now() unless a `now` is passed. Every function
   returns '' on null/undefined/rubbish input and never throws —
   the screen must degrade to blank lines, never to a crash or a
   zero that reads as "clean".

   Doctrine carried in the wording: the tense follows the age of
   the data ("this week" only while the data is live or recent);
   unmeasured is never clean; a list that failed to load is never
   "nothing needs you".
   ═══════════════════════════════════════════════════════════════ */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/* ISO day part → { y, m, d } or null. Accepts 'YYYY-MM-DD' with an optional
   time after it, and rejects impossible calendar dates (2026-13-40). */
function parseIsoDay(iso) {
  if (typeof iso !== 'string') return null;
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ]|$)/);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (mo < 1 || mo > 12 || d < 1 || d > 31) return null;
  // Round-trip through UTC to reject 31 Feb and friends without touching
  // the local zone.
  const probe = new Date(Date.UTC(y, mo - 1, d));
  if (probe.getUTCFullYear() !== y || probe.getUTCMonth() !== mo - 1 || probe.getUTCDate() !== d) return null;
  return { y, m: mo, d, weekday: probe.getUTCDay() };
}

/** '2026-09-06' → '6 Sep'. '' on anything unparseable. */
export function fmtDateWords(iso) {
  const p = parseIsoDay(iso);
  return p ? `${p.d} ${MONTHS[p.m - 1]}` : '';
}

/** '2026-09-06' → 'Sun 6 Sep'. '' on anything unparseable. */
export function fmtDayWords(iso) {
  const p = parseIsoDay(iso);
  return p ? `${DAYS[p.weekday]} ${p.d} ${MONTHS[p.m - 1]}` : '';
}

/** Minutes as words: 580 → '9h 40m', 600 → '10h', 40 → '40m', 0 → '0m'.
    Rounded to whole minutes FIRST, then split, so 59.6 reads '1h' and never
    '0h 60m'. '' on rubbish; a negative clamps to '0m' rather than inventing
    time owed. Shared by the calendar's bar title and the Tomorrow short
    line, so the two surfaces read the same. */
export function fmtMins(mins) {
  if (typeof mins !== 'number' || !Number.isFinite(mins)) return '';
  const total = Math.max(0, Math.round(mins));
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h === 0) return `${m}m`;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

/** The LOCAL calendar day as 'YYYY-MM-DD'. toISOString() would give the UTC
    day, which after 23:00 BST is tomorrow — wrong for a header that says
    "Today". '' on an invalid Date. */
export function todayIsoLocal(now = new Date()) {
  if (!(now instanceof Date) || isNaN(now.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

function isStale(asOf) {
  return Boolean(asOf) && asOf.band === 'stale';
}

/**
 * The hero's label. Live/recent: "Drivers clear this week · to Sun 6 Sep".
 * Stale: "Drivers clear · to 20 Aug" — the tense drops "this week" because
 * the week shown is not this one, and the date is the card data's, which is
 * what the count was actually measured to. A date that was not supplied is
 * simply not shown; nothing is invented.
 */
export function heroLabel(week, asOf) {
  if (!week && !asOf) return '';
  if (isStale(asOf)) {
    const to = fmtDateWords(asOf.card_data_to);
    return to ? `Drivers clear · to ${to}` : 'Drivers clear';
  }
  const to = week ? fmtDayWords(week.to) : '';
  return to ? `Drivers clear this week · to ${to}` : 'Drivers clear this week';
}

/** "" unless stale; then "The 18 days since are not checked." */
export function staleNote(asOf) {
  if (!isStale(asOf)) return '';
  const n = Number(asOf.days_old);
  if (!Number.isFinite(n) || n <= 0) return 'The days since are not checked.';
  return `The ${n} days since are not checked.`;
}

function firstNameOf(w) {
  if (!w) return '';
  const first = typeof w.first_name === 'string' ? w.first_name.trim() : '';
  if (first) return first;
  const name = typeof w.name === 'string' ? w.name.trim() : '';
  return name ? name.split(/\s+/)[0] : '';
}

/** The thank-you the manager pastes to the driver in worth_a_word. */
export function thankYouText(w) {
  const first = firstNameOf(w);
  const n = w ? Number(w.current) : NaN;
  if (!first || !Number.isFinite(n) || n <= 0) return '';
  return `Thanks ${first}. ${n} clean days in a row, no limits gone over, no speeding, downloads on time. It's noticed.`;
}

/* ── Sending the thanks to the driver's phone ───────────────────
   The words on and around the send button. The server does the
   sending (POST /api/today/thanks) and says why when it cannot;
   these only put its answer into words. The first name is the
   same one thankYouText uses. */

function whoOf(first) {
  return typeof first === 'string' && first.trim() ? first.trim() : '';
}

/** The send button's label by state: 'idle' | 'sending' | 'sent' | 'failed'.
    A failed send offers the button again, so it reads as idle. */
export function thanksButtonLabel(state, first) {
  const who = whoOf(first);
  if (state === 'sending') return 'Sending…';
  if (state === 'sent') return who ? `Sent to ${who}'s phone` : "Sent to the driver's phone";
  return who ? `Send ${who} a thanks` : 'Send a thanks';
}

/** The server's refusal, in words. Unknown reasons get one honest line. */
export function thanksFailureText(reason, first) {
  const who = whoOf(first) || 'This driver';
  switch (reason) {
    case 'no_ddir_user':
    case 'ddir_unknown_driver':
      return `${who} isn't on the driver app yet`;
    case 'org_unmapped':
      return "This yard isn't linked to the driver app";
    case 'ddir_unavailable':
      return "Couldn't reach the driver app";
    case 'no_streak':
      return 'Nothing to thank yet';
    default:
      return "Couldn't send the thanks";
  }
}

/** "Sent today" / "Sent yesterday" / "Sent 2 days ago" from an ISO stamp,
    by LOCAL calendar days. '' on nothing or rubbish; a stamp in the future
    (clock skew) reads as today rather than a negative. */
export function sentAgoText(iso, now = new Date()) {
  if (typeof iso !== 'string' || !iso) return '';
  const sent = new Date(iso);
  if (isNaN(sent.getTime())) return '';
  if (!(now instanceof Date) || isNaN(now.getTime())) return '';
  const day = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const n = Math.round((day(now) - day(sent)) / 86400000);
  if (n <= 0) return 'Sent today';
  if (n === 1) return 'Sent yesterday';
  return `Sent ${n} days ago`;
}

/* ── The weekly yard high five ──────────────────────────────────
   Weekly, never daily (Zed, 2026-09-11): daily is a state on the
   driver's screen, not a message. The server says whether the week
   earns one (`week_high_five.eligible`), sends it (POST
   /api/today/highfive) and says why when it cannot; these put the
   block and its answers into words. */

/** "Everyone clear this week · 11 of 11". '' unless the week is eligible. */
export function highFiveLine(hf) {
  if (!hf || !hf.eligible) return '';
  const clean = Number(hf.clean);
  const out = Number(hf.out);
  if (!Number.isFinite(clean) || !Number.isFinite(out) || out <= 0) return '';
  return `Everyone clear this week · ${clean} of ${out}`;
}

/** The high-five button's label by state: 'idle' | 'sending' | 'sent' |
    'failed'. Sent is dated to the week it was about; a failed send offers
    the button again. */
export function highFiveButtonLabel(state, hf) {
  if (state === 'sending') return 'Sending…';
  if (state === 'sent') {
    const to = hf ? fmtDayWords(hf.week_to) : '';
    return to ? `High fived · week to ${to}` : 'High fived';
  }
  return 'High five the yard';
}

/** The server's refusal, in words. Unknown reasons get one honest line. */
export function highFiveFailureText(reason) {
  switch (reason) {
    case 'already_sent':
      return 'Already sent this week';
    case 'org_unmapped':
      return "This yard isn't linked to the driver app";
    case 'no_recipients':
    case 'ddir_unknown_driver':
      return 'No drivers on the app yet';
    case 'ddir_unavailable':
      return "Couldn't reach the driver app";
    case 'not_eligible':
      return 'Not everyone is clear this week';
    default:
      return "Couldn't send the high five";
  }
}

/** True when the week shown is the week the last high five was about —
    the card opens on "High fived" rather than offering it twice. */
export function highFiveSentThisWeek(hf) {
  if (!hf || typeof hf.week_to !== 'string' || !hf.week_to) return false;
  return hf.sent_week_to === hf.week_to;
}

/**
 * "Card data to 6 Sep · truck data to 6 Sep" plus " · uploaded by hand" when
 * the org has said its downloads are NOT automatic. An absent auto_download
 * claims nothing about the channel. A side with no date is left out.
 */
export function freshnessLine(asOf) {
  if (!asOf) return '';
  const card = fmtDateWords(asOf.card_data_to);
  const truck = fmtDateWords(asOf.truck_data_to);
  const parts = [];
  if (card) parts.push(`Card data to ${card}`);
  if (truck) parts.push(parts.length ? `truck data to ${truck}` : `Truck data to ${truck}`);
  if (!parts.length) return '';
  if (asOf.auto_download === false) parts.push('uploaded by hand');
  return parts.join(' · ');
}

/**
 * While the server is still building the org's speed-coverage cache (a big
 * org's first Today after an upload), every driven day reads "not measured".
 * Say why, in one muted sentence; nothing at all once it is ready or when the
 * server did not state it.
 */
export function speedCoverageNote(asOf, { polling = false } = {}) {
  if (!asOf || asOf.speed_coverage !== 'building') return '';
  const note = 'Speed data is still being checked, so drivers show as not measured for now.';
  return polling ? `${note} Checking again…` : note;
}

/* ── Re-reading Today while the cache builds ───────────────────────
   The server builds the speed-coverage cache in the background and is
   ready seconds to a minute later, so the screen re-reads /api/today
   on a short schedule rather than waiting for a manual reload. The
   schedule lives here so it can be tested without a clock. */

/** True only while the loaded payload says the cache is still building. */
export function shouldPollCoverage(payload) {
  return Boolean(payload) && typeof payload === 'object' && payload.as_of?.speed_coverage === 'building';
}

/** Delay before poll number `attempt` (0-based): 8 s first, then 15 s, and
    null after eight attempts — the screen stops asking and leaves the note.
    Null on anything that is not a whole non-negative number. */
export function nextCoveragePollMs(attempt) {
  if (!Number.isInteger(attempt) || attempt < 0) return null;
  if (attempt === 0) return 8000;
  return attempt < 8 ? 15000 : null;
}

/**
 * The hero's sub-line under the number: how much of the worklist is waiting.
 * A list that failed to load with nothing in it is stated as such — an empty
 * list with `unavailable` set is never "Nothing needs you".
 */
/* Andre's morning line, built from the worklist beside it and nothing
   else — the same rows, in the same order, so the memo can never name a
   number the list does not show. Infringement rows (key 'inf-…') fold
   into one clause; every other row keeps its own title. */
export function andreLine(items, { unavailable = false } = {}) {
  if (unavailable) return "The list couldn't load. Refresh, or check the downloads and notes by hand.";
  const list = Array.isArray(items) ? items.filter((r) => r && typeof r === 'object') : [];
  if (list.length === 0) return 'Nothing needs you today.';
  const infCount = list.filter((r) => String(r.key || '').startsWith('inf-')).length;
  const clauses = [];
  let infDone = false;
  let covered = 0;
  for (const r of list) {
    if (clauses.length === 2) break;
    if (String(r.key || '').startsWith('inf-')) {
      if (infDone) continue;
      infDone = true;
      covered += infCount;
      clauses.push(`${infCount} infringement${infCount === 1 ? '' : 's'} to send or chase`);
    } else {
      const t = String(r.title || '').trim();
      if (!t) continue;
      covered += 1;
      clauses.push(t.charAt(0).toLowerCase() + t.slice(1));
    }
  }
  if (clauses.length === 0) return `${list.length} thing${list.length === 1 ? '' : 's'} need${list.length === 1 ? 's' : ''} you. Start at the top of the list.`;
  const rest = Math.max(0, list.length - covered);
  const tail = rest > 0 ? ` ${rest} more below.` : '';
  if (clauses.length === 1) {
    return list.length === 1 ? `One thing needs you: ${clauses[0]}.` : `Start with ${clauses[0]}.${tail}`;
  }
  return `Start with ${clauses[0]}, then ${clauses[1]}.${tail}`;
}

export function needsYouLine(count, { unavailable = false } = {}) {
  const n = Number(count);
  const k = Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
  if (k === 0) return unavailable ? "The list couldn't load" : 'Nothing needs you today';
  return `${k} need${k === 1 ? 's' : ''} you today`;
}
